import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import BusinessInfoSection from './components/BusinessInfoSection';
import LocationContactSection from './components/LocationContactSection';
import MediaSection from './components/MediaSection';
import BusinessPreview from './components/BusinessPreview';
import { useAuth } from '../../contexts/AuthContext';
import { businessFormService } from '../../services/businessFormService';

const AddBusiness = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    // Business Information
    businessName: '',
    businessDescription: '',
    category: '',
    logo: null,
    logoPreview: null,
    heroImage: null,
    heroImagePreview: null,
    
    // Key Features
    keyFeatures: [
      'Qualified and experienced practitioners',
      'Peaceful and calming environment',
      'Personalised treatment plans'
    ],
    
    // Certifications & Qualifications
    certifications: [],
    
    // Rating (if they have one)
    rating: null,
    reviewCount: 0,
    
    // Location & Contact
    location: {
      address: '',
      city: '',
      postcode: '',
      fullAddress: '',
      showMap: true,
      openingHours: {
        monday: '',
        tuesday: '',
        wednesday: '',
        thursday: '',
        friday: '',
        saturday: '',
        sunday: ''
      }
    },
    
    // Contact Information
    contact: {
      phone: '',
      email: '',
      website: ''
    },
    
    // Social Media
    socialMedia: {
      facebook: '',
      instagram: '',
      x: '',
      linkedin: ''
    },
    
    // Additional Features
    features: {
      parkingAvailable: false,
      wheelchairAccessible: false,
      publicTransportNearby: false,
      onlineBooking: false
    }
  });

  const steps = [
    { id: 1, title: 'Business Info', icon: 'Building2' },
    { id: 2, title: 'Location & Contact', icon: 'MapPin' },
    { id: 3, title: 'Media & Images', icon: 'Image' }
  ];

  // Check authentication on mount
  useEffect(() => {
    if (!user) {
      navigate('/login', { 
        state: { from: '/add-business' }
      });
    } else {
      // Check if user already has a business
      checkExistingBusiness();
    }
  }, [user, navigate]);

  const checkExistingBusiness = async () => {
    if (!user) return;

    try {
      const { data: existingBusinesses } = await businessFormService?.checkExistingBusiness(user?.id);

      if (existingBusinesses?.length > 0) {
        navigate('/user-profile', {
          state: {
            message: 'You already have a business listing. You can edit your existing business instead.',
            type: 'warning'
          }
        });
      }
    } catch (error) {
      console.error('Error checking existing business:', error);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData?.businessName?.trim()) newErrors.businessName = 'Business name is required';
        if (!formData?.businessDescription?.trim()) newErrors.businessDescription = 'Business description is required';
        if (!formData?.category) newErrors.category = 'Business category is required';
        if (formData?.contact?.email && 
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.contact?.email)) {
          newErrors['contact.email'] = 'Please enter a valid email address';
        }
        break;

      case 2:
        if (!formData?.location?.address?.trim()) newErrors['location.address'] = 'Address is required';
        if (!formData?.location?.city?.trim()) newErrors['location.city'] = 'City is required';
        if (!formData?.location?.postcode?.trim()) {
          newErrors['location.postcode'] = 'Postcode is required';
        } else if (!/^[A-Z]{1,2}[0-9R][0-9A-Z]? [0-9][A-Z]{2}$/?.test(formData?.location?.postcode)) {
          newErrors['location.postcode'] = 'Please enter a valid UK postcode';
        }
        if (!formData?.contact?.phone?.trim()) {
          newErrors['contact.phone'] = 'Phone number is required';
        } else if (!/^[\+]?[0-9\s\-\(\)]{10,}$/?.test(formData?.contact?.phone)) {
          newErrors['contact.phone'] = 'Please enter a valid phone number';
        }
        if (!formData?.contact?.email?.trim()) {
          newErrors['contact.email'] = 'Email address is required';
        }
        break;

      case 3:
        // Media validation is optional but logo is recommended
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps?.length) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (isDraft = false) => {
    if (!isDraft && !validateAllSteps()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { data, error } = await businessFormService?.submitBusinessWithDetails(formData);
      
      if (error) {
        setErrors({ submit: error });
        return;
      }
      
      // Success - redirect to profile or confirmation
      navigate('/user-profile', { 
        state: { 
          message: isDraft 
            ? 'Business saved as draft successfully!' :'Business submitted for review successfully!',
          businessId: data?.id
        }
      });
    } catch (error) {
      console.error('Error submitting business:', error);
      setErrors({ submit: 'Failed to submit business. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateAllSteps = () => {
    let isValid = true;
    for (let i = 1; i <= steps?.length; i++) {
      if (!validateStep(i)) {
        isValid = false;
      }
    }
    return isValid;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <BusinessInfoSection formData={formData} setFormData={setFormData} errors={errors} />;
      case 2:
        return <LocationContactSection formData={formData} setFormData={setFormData} errors={errors} />;
      case 3:
        return <MediaSection formData={formData} setFormData={setFormData} errors={errors} />;
      default:
        return null;
    }
  };

  if (!user) {
    return null; // Will redirect to login
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <button
                onClick={() => navigate('/user-profile')}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-gentle"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">Add Your Business</h1>
                <p className="text-muted-foreground mt-1">
                  Create your business profile on our holistic directory (Free)
                </p>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center space-x-4 overflow-x-auto pb-2">
              {steps?.map((step, index) => (
                <div key={step?.id} className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 min-w-0">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        currentStep >= step?.id
                          ? 'bg-primary text-white' :'bg-muted text-muted-foreground'
                      }`}
                    >
                      <Icon name={step?.icon} size={16} />
                    </div>
                    <span
                      className={`text-sm font-medium whitespace-nowrap ${
                        currentStep >= step?.id ? 'text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      {step?.title}
                    </span>
                  </div>
                  {index < steps?.length - 1 && (
                    <div
                      className={`w-8 h-0.5 transition-colors ${
                        currentStep > step?.id ? 'bg-primary' : 'bg-border'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {renderStepContent()}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between pt-6 border-t border-border">
                <div>
                  {currentStep > 1 && (
                    <Button
                      variant="outline"
                      onClick={handlePrevious}
                      iconName="ChevronLeft"
                      iconPosition="left"
                      disabled={isSubmitting}
                    >
                      Previous
                    </Button>
                  )}
                </div>

                <div className="flex items-center space-x-3">
                  {currentStep === steps?.length && (
                    <Button
                      variant="outline"
                      onClick={() => handleSubmit(true)}
                      loading={isSubmitting}
                      iconName="Save"
                      iconPosition="left"
                      disabled={isSubmitting}
                    >
                      Save Draft
                    </Button>
                  )}
                  
                  {currentStep < steps?.length ? (
                    <Button
                      variant="default"
                      onClick={handleNext}
                      iconName="ChevronRight"
                      iconPosition="right"
                      disabled={isSubmitting}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      onClick={() => handleSubmit(false)}
                      loading={isSubmitting}
                      iconName="Send"
                      iconPosition="left"
                      disabled={isSubmitting}
                    >
                      Submit Business
                    </Button>
                  )}
                </div>
              </div>

              {errors?.submit && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertCircle" size={16} className="text-destructive" />
                    <p className="text-sm text-destructive">{errors?.submit}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Preview Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-heading font-semibold text-foreground">Preview</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPreview(!showPreview)}
                    iconName={showPreview ? "EyeOff" : "Eye"}
                    iconPosition="left"
                    className="lg:hidden"
                  >
                    {showPreview ? 'Hide' : 'Show'}
                  </Button>
                </div>
                
                <div className={`${showPreview ? 'block' : 'hidden lg:block'}`}>
                  <BusinessPreview formData={formData} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AddBusiness;